/**
 * Workspace-scoped PostHog flag readiness.
 *
 * General flag readiness lives in `posthog-flags.ts`; this module tracks
 * whether flags have been re-evaluated AFTER `workspace_id` was published
 * via `setPersonPropertiesForFlags`.
 */
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import posthog from 'posthog-js';

const WORKSPACE_FLAG_SYNC_TIMEOUT_MS = 5000;

// TELEMETRY-GATE (design D6): when telemetry is off, PostHog is never
// initialized — workspace-scoped flags are "synced" immediately.
export const posthogWorkspaceFlagsSyncedAtom = atom(!isSaasTelemetryEnabled);

// Lazy: module-level getDefaultStore() breaks tests that partial-mock jotai.
let jotaiStore: ReturnType<typeof getDefaultStore> | undefined;
const getJotaiStore = (): ReturnType<typeof getDefaultStore> => {
  jotaiStore ??= getDefaultStore();
  return jotaiStore;
};

let activeSyncTimeout: ReturnType<typeof setTimeout> | null = null;
let activeSyncUnsubscribe: (() => void) | null = null;
let activeSyncGeneration = 0;

const clearActiveWorkspaceFlagSync = (): void => {
  if (activeSyncTimeout !== null) {
    clearTimeout(activeSyncTimeout);
    activeSyncTimeout = null;
  }
  activeSyncUnsubscribe?.();
  activeSyncUnsubscribe = null;
};

/**
 * Publish `workspace_id` for eval-time flag targeting and reload flags.
 * Resets {@link posthogWorkspaceFlagsSyncedAtom} until `onFeatureFlags` fires
 * (or the sync times out fail-open).
 */
export function syncWorkspaceIdToPosthogFlags(workspaceId: string): void {
  if (!isSaasTelemetryEnabled) return;

  clearActiveWorkspaceFlagSync();
  getJotaiStore().set(posthogWorkspaceFlagsSyncedAtom, false);

  // Reload ourselves: setPersonPropertiesForFlags(true) would start a fetch
  // before the listener is attached, and a second reloadFeatureFlags() would
  // race an in-flight boot `/flags` against the workspace-scoped one.
  posthog.setPersonPropertiesForFlags({ workspace_id: workspaceId }, false);

  // posthog-js may invoke the listener synchronously with pre-reload flags when
  // flags were already loaded — ignore callbacks until registration returns.
  let acceptCallbacks = false;
  const syncGeneration = ++activeSyncGeneration;
  activeSyncUnsubscribe = posthog.onFeatureFlags(() => {
    if (!acceptCallbacks) return;
    if (syncGeneration !== activeSyncGeneration) return;

    logger.debug(
      '[posthog-workspace-flags] workspace-scoped flags reloaded for workspace_id:',
      workspaceId
    );
    getJotaiStore().set(posthogWorkspaceFlagsSyncedAtom, true);
    clearActiveWorkspaceFlagSync();
  });
  acceptCallbacks = true;

  posthog.reloadFeatureFlags();

  activeSyncTimeout = setTimeout(() => {
    if (syncGeneration !== activeSyncGeneration) return;

    logger.warn(
      '[posthog-workspace-flags] workspace flag sync timed out after 5s, rendering anyway'
    );
    getJotaiStore().set(posthogWorkspaceFlagsSyncedAtom, true);
    clearActiveWorkspaceFlagSync();
  }, WORKSPACE_FLAG_SYNC_TIMEOUT_MS);
}

/**
 * Fail-open when hydration settles with no workspace to publish — person-level
 * flags are authoritative and holding the gate would exclude the cohort forever.
 */
export function failOpenPosthogWorkspaceFlagsSynced(): void {
  if (!isSaasTelemetryEnabled) return;

  clearActiveWorkspaceFlagSync();
  getJotaiStore().set(posthogWorkspaceFlagsSyncedAtom, true);
}

/** Reset workspace-scoped flag sync state (logout). */
export function resetPosthogWorkspaceFlagsSynced(): void {
  clearActiveWorkspaceFlagSync();
  getJotaiStore().set(posthogWorkspaceFlagsSyncedAtom, !isSaasTelemetryEnabled);
}

/**
 * True once PostHog flags have been re-evaluated with the active workspace_id.
 * Workspace-scoped flags must gate on this, not
 * {@link posthogFlagsReadyAtom} alone — the first `/flags` call runs before
 * workspace hydration.
 *
 * No production consumer today (the Clinician Max beta getter was the last).
 * Kept so the next workspace-scoped flag can `useAtomValue` /
 * `posthogWorkspaceFlagsSyncedAtom` without re-deriving sync semantics; prefer
 * reading the atom directly over reintroducing a one-line wrapper.
 */
export function usePosthogWorkspaceFlagsSynced(): boolean {
  return useAtomValue(posthogWorkspaceFlagsSyncedAtom);
}
